import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import { CurrencyLabel } from '@/components/Common/CurrencyFlag';
import {
  formatAmount,
  groupAmountSummary,
  totalByCurrency,
  type ExpenseAmountSummaryRow,
} from '@/utils/approval/expensePreApproval';

type AmountSummaryTableProps = {
  /** null = 아직 계산 전 */
  rows: ExpenseAmountSummaryRow[] | null;
  /** 비었을 때 — 작성 'zero'(빈 행 + 0 KRW) / 조회 'noData'('데이터가 없습니다.'). 레거시 동일 */
  emptyMode?: 'zero' | 'noData';
};

/** 205 「지급 총액」 표 — 업체명 rowspan, 통화별 총 지급액 tfoot. 작성·조회 공용 */
const AmountSummaryTable = ({
  rows,
  emptyMode = 'zero',
}: AmountSummaryTableProps) => {
  const { t } = useTranslation();
  const list = rows ?? [];
  const groups = groupAmountSummary(list);
  const totals = totalByCurrency(list);
  const footRows = totals.length ? totals : [{ currency: 'KRW', amount: 0 }];

  const renderEmpty = () =>
    emptyMode === 'noData' ? (
      <tr>
        <td colSpan={4} className="align-center">
          {t('데이터가 없습니다.')}
        </td>
      </tr>
    ) : (
      <tr>
        <td />
        <td />
        <td className="align-right">0</td>
        <td className="align-center">
          <CurrencyLabel currency="KRW" />
        </td>
      </tr>
    );

  return (
    <ApprovalFormTable className="expense-amount-summary-table">
      <colgroup>
        <col />
        <col />
        <col style={{ width: 250 }} />
        <col style={{ width: 96 }} />
      </colgroup>
      <thead>
        <tr>
          <th>{t('업체명')}</th>
          <th>{t('헤더적요')}</th>
          <th>{t('총액 (VAT별도)')}</th>
          <th>{t('통화')}</th>
        </tr>
      </thead>
      <tbody>
        {groups.length === 0
          ? renderEmpty()
          : groups.map((group) => (
              <Fragment key={group.vendor}>
                {group.rows.map((row, index) => (
                  <tr key={`${row.summary}-${row.currency}`}>
                    {index === 0 && (
                      <td rowSpan={group.rows.length}>{group.vendor}</td>
                    )}
                    <td>{row.summary}</td>
                    <td className="align-right">
                      {formatAmount(row.amount, row.currency)}
                    </td>
                    <td className="align-center">
                      <CurrencyLabel currency={row.currency} />
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
      </tbody>
      <tfoot>
        {footRows.map((total, index) => (
          <tr key={total.currency}>
            {index === 0 && (
              <>
                <td rowSpan={footRows.length}>
                  <strong>{t('총 지급액')}</strong>
                </td>
                <td rowSpan={footRows.length} />
              </>
            )}
            <td className="align-right">
              <strong>{formatAmount(total.amount, total.currency)}</strong>
            </td>
            <td className="align-center">
              <CurrencyLabel currency={total.currency} />
            </td>
          </tr>
        ))}
      </tfoot>
    </ApprovalFormTable>
  );
};

export default AmountSummaryTable;
